/**
 * The guide's plain text and links (ADR-049). Rook writes text in Ops the same way as the onsite
 * content: "- " is a bullet, "1. " a numbered step, **bold** and *italic*; any other line is a
 * paragraph. It becomes React elements, never HTML, so nothing typed in Ops can inject markup.
 */
import { Fragment, type ReactNode } from 'react';
import type { Content, GuideEntry, GuideTab } from './types';

export type Block = { p: string } | { ul: string[] } | { ol: string[] };

export function blocks(text: string): Block[] {
  const out: Block[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    const step = /^\d+[.)]\s+(.*)$/.exec(line);
    const last = out[out.length - 1];
    if (bullet) {
      if (last && 'ul' in last) last.ul.push(bullet[1] ?? '');
      else out.push({ ul: [bullet[1] ?? ''] });
    } else if (step) {
      if (last && 'ol' in last) last.ol.push(step[1] ?? '');
      else out.push({ ol: [step[1] ?? ''] });
    } else if (line) {
      out.push({ p: line });
    }
  }
  return out;
}

/** **bold** and *italic* inside one line. */
export function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export function GuideText({ text }: { text: string }) {
  return (
    <div className="g-text">
      {blocks(text).map((b, i) =>
        'p' in b ? (
          <p key={i}>{inline(b.p)}</p>
        ) : 'ul' in b ? (
          <ul key={i}>
            {b.ul.map((x, j) => (
              <li key={j}>{inline(x)}</li>
            ))}
          </ul>
        ) : (
          <ol key={i}>
            {b.ol.map((x, j) => (
              <li key={j}>{inline(x)}</li>
            ))}
          </ol>
        ),
      )}
    </div>
  );
}

/**
 * A Google Maps search for a place (V1's getGoogleMapsUrl): a street address as typed, or the
 * place's name plus its area when the address is only "Main Street, Central City".
 */
export function mapsUrl(address: string, title = ''): string {
  const addr = (address.split('•')[0] ?? '').replace(/\(.*?\)/g, '').trim();
  const first = (s: string, sep: string) => s.split(sep)[0] ?? s;
  const name = first(first(title.replace(/⭐️|🏠|®/gu, ''), ' at '), ' / ')
    .replace(/\(.*?\)/g, '')
    .trim();
  const withState = /\b(co|colorado)\b/i.test(addr) ? addr : `${addr}, CO`;
  const query = /^\d/.test(addr) || !name ? withState : `${name}, ${withState}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** Web links open in a new tab; tel:, sms: and mailto: hand off to the phone. */
export function linkProps(href: string) {
  return /^https?:/.test(href) ? { href, target: '_blank', rel: 'noopener noreferrer' } : { href };
}

export interface Hit {
  sectionId: string;
  sectionTitle: string;
  groupTitle: string;
  entry: GuideEntry;
}

/** Every entry in a tab whose words include all of the search's words. */
export function search(tab: GuideTab, query: string): Hit[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hits: Hit[] = [];
  for (const s of tab.sections)
    for (const g of s.groups)
      for (const e of g.entries) {
        const hay = [e.title, e.subtitle, e.where, e.body, e.address, g.title, ...e.tags].join(' ').toLowerCase();
        if (words.every((w) => hay.includes(w))) hits.push({ sectionId: s.id, sectionTitle: s.title, groupTitle: g.title, entry: e });
      }
  return hits;
}

/** My Stay's page for getting in (the door and the lock), when the guide has one. */
export function doorPage(c: Content | undefined): string | null {
  for (const s of c?.stay.sections ?? []) {
    if (s.layout !== 'pages') continue;
    const g = s.groups.find((x) => x.id === 'entry' || x.icon === 'key');
    if (g) return `/my-stay/${s.id}/${g.id}`;
  }
  return null;
}

/** A House Guide section's name as Rook has it, for links into it. */
export function sectionTitle(c: Content | undefined, id: string, fallback: string): string {
  return c?.house.sections.find((s) => s.id === id)?.title || fallback;
}
